import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  companyIdSelector,
  errors,
  isTranslatableItem,
  Log,
  mapAppLanguageToHelpTopicLocale,
  type HelpTopicContentLocale,
} from "@ryde/shared";

import {
  buildHelpTopicAssetsBaseUrl,
  fetchHelpTopicContent,
} from "./help-topic-content.utils";

interface IHelpTopicLoadedContent {
  markdown: string;
  /** Absolute URL of the topic's assets folder on the API, for the markdown's relative image links. */
  markdownBasePath: string;
  /** The locale actually served; Hebrew falls back to English on the backend. */
  locale: HelpTopicContentLocale;
  hasHebrewTranslation: boolean;
}

interface IHelpTopicContentState extends IHelpTopicLoadedContent {
  isLoading: boolean;
  error: string | null;
  setLocale: (locale: HelpTopicContentLocale) => void;
}

/** The language switcher's pick, remembered per topic so a new topic starts from the page language again. */
interface IHelpTopicLocaleOverride {
  helpTopikUrl: string;
  locale: HelpTopicContentLocale;
}

const EMPTY_CONTENT: IHelpTopicLoadedContent = {
  markdown: "",
  markdownBasePath: "",
  locale: "en",
  hasHebrewTranslation: false,
};

export const useHelpTopicContent = (
  helpTopikUrl: string | undefined,
): IHelpTopicContentState => {
  const { t, i18n } = useTranslation();
  const companyId = useRecoilValue(companyIdSelector);
  const pageLocale = mapAppLanguageToHelpTopicLocale(i18n.language);

  const [localeOverride, setLocaleOverride] = useState<
    IHelpTopicLocaleOverride | undefined
  >(undefined);
  const [content, setContent] =
    useState<IHelpTopicLoadedContent>(EMPTY_CONTENT);
  const [isLoading, setIsLoading] = useState(Boolean(helpTopikUrl));
  const [error, setError] = useState<string | null>(null);

  const requestedLocale =
    localeOverride && localeOverride.helpTopikUrl === helpTopikUrl
      ? localeOverride.locale
      : pageLocale;

  const setLocale = useCallback(
    (nextLocale: HelpTopicContentLocale): void => {
      if (helpTopikUrl) {
        setLocaleOverride({ helpTopikUrl, locale: nextLocale });
      }
    },
    [helpTopikUrl],
  );

  useEffect(() => {
    if (!helpTopikUrl) {
      setContent(EMPTY_CONTENT);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (!companyId) {
      Log.warn("useHelpTopicContent: companyId is not loaded yet");
      return;
    }

    let cancelled = false;

    const loadContent = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);

      const result = await fetchHelpTopicContent({
        companyId,
        helpTopikUrl,
        locale: requestedLocale,
      });

      if (cancelled) {
        return;
      }

      if (!result.success || !result.data) {
        setContent(EMPTY_CONTENT);
        setError(
          t(
            isTranslatableItem(result.error)
              ? result.error.translationKey
              : errors.helpTopics.topicNotFound,
          ),
        );
        setIsLoading(false);
        return;
      }

      setContent({
        markdown: result.data.markdown,
        markdownBasePath: buildHelpTopicAssetsBaseUrl({
          companyId,
          content: result.data,
        }),
        locale: result.data.locale,
        hasHebrewTranslation: result.data.hasHebrewTranslation,
      });
      setIsLoading(false);
    };

    void loadContent();

    return () => {
      cancelled = true;
    };
  }, [helpTopikUrl, companyId, requestedLocale, t]);

  return {
    ...content,
    isLoading,
    error,
    setLocale,
  };
};
